import type { AdminHealthRow, HealthState } from '@pa/api/admin'
import { useAdminListHealth, useAdminListIncidents } from '@pa/api/admin'
import { FilterMenu } from '@pa/ui'
import { keepPreviousData } from '@tanstack/react-query'
import { Activity, Building2, Search } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { useNow } from '../hooks/useNow'
import { heartbeatAge } from '../model/heartbeat'
import { HISTORY_DAYS, SHORT_STATE, STATUS_LABEL } from '../model/labels'
import { pctFromRatio } from '../model/uptime'
import { IncidentList } from './IncidentList'
import { UptimeStrip } from './UptimeBars'

/** S-07's search params (Frontend §7.1): `attention`, `q`, `clinic` (incident filter), `page` (incidents). */
export interface HealthOverviewSearch {
  attention?: boolean
  q?: string
  clinic?: string
  page?: number
}

export function parseHealthOverviewSearch(raw: Record<string, unknown>): HealthOverviewSearch {
  const out: HealthOverviewSearch = {}
  if (raw.attention === true || raw.attention === 'true') out.attention = true
  if (typeof raw.q === 'string' && raw.q.trim() !== '') out.q = raw.q.trim().slice(0, 100)
  if (typeof raw.clinic === 'string' && /^[0-9a-f-]{36}$/i.test(raw.clinic)) out.clinic = raw.clinic
  const page = Number(raw.page)
  if (Number.isInteger(page) && page > 1) out.page = page
  return out
}

const STATE_CLASS: Record<HealthState, string> = { up: 'ok', degraded: 'warn', down: 'down', unknown: 'idle' }

export interface HealthOverviewProps {
  search: HealthOverviewSearch
  onSearch: (next: HealthOverviewSearch, opts?: { replace?: boolean }) => void
  /** The incident history's Clinic filter (#38). */
  clinics?: readonly { id: string; name: string }[] | undefined
  /** Row click → the clinic's S-04 Health tab. */
  onOpenClinic: (clinicId: string) => void
  /** The clinic name as a link to S-04 Health (keyboard and screen readers). */
  renderClinicLink: (clinic: { id: string; name: string }) => ReactNode
  /** The viewer's time zone (cross-clinic times, API.md §1.4). */
  timeZone: string
}

/**
 * S-07 Health, all clinics (UserFlows S-07; Frontend §7.2): one row per active clinic with the keeper and session
 * state, last heartbeat, 90-day `overall` uptime and its compact strip (D-38); "Needs attention only" and search;
 * then the incident history across clinics with a Clinic column and filter. Row click → S-04 Health.
 */
export function HealthOverview({
  search,
  onSearch,
  clinics,
  onOpenClinic,
  renderClinicLink,
  timeZone,
}: HealthOverviewProps) {
  const now = useNow()
  const [q, setQ] = useState(search.q ?? '')
  const rows = useAdminListHealth(
    { ...(search.attention ? { attention: 'true' as const } : {}), ...(search.q ? { q: search.q } : {}) },
    { query: { placeholderData: keepPreviousData, refetchInterval: 60_000 } },
  )
  const incidents = useAdminListIncidents(
    { ...(search.clinic ? { clinicId: search.clinic } : {}), ...(search.page ? { page: String(search.page) } : {}) },
    { query: { placeholderData: keepPreviousData } },
  )

  // Typing updates the URL with replace, debounced by 300 ms (Frontend §6.1).
  useEffect(() => {
    const trimmed = q.trim()
    if (trimmed === (search.q ?? '')) return
    const t = setTimeout(() => {
      onSearch({ ...search, q: trimmed || undefined }, { replace: true })
    }, 300)
    return () => {
      clearTimeout(t)
    }
  }, [q, search, onSearch])

  const data = rows.data
  const filtered = Boolean(search.q) || Boolean(search.attention)
  const list = incidents.data

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Health</h1>
          <p>PF Keeper and the Practice Fusion session at every clinic, over the last {HISTORY_DAYS} days.</p>
        </div>
      </div>
      <div className="card">
        <div className="toolbar filters">
          <div className="search input-wrap">
            <Search aria-hidden />
            <input
              className="input"
              type="search"
              aria-label="Search clinics"
              placeholder="Search by name or Clinic ID"
              value={q}
              onChange={(e) => {
                setQ(e.target.value)
              }}
            />
          </div>
          <label className="check-inline">
            <input
              type="checkbox"
              checked={search.attention === true}
              onChange={(e) => {
                onSearch({ ...search, attention: e.target.checked ? true : undefined }, { replace: true })
              }}
            />{' '}
            Needs attention only
          </label>
        </div>
        {rows.isPending ? (
          <div className="empty" role="status">
            Loading clinics…
          </div>
        ) : rows.isError ? (
          <div className="empty" role="alert">
            <div>Couldn&apos;t load the clinics&apos; health.</div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => void rows.refetch()}>
              Retry
            </button>
          </div>
        ) : !data ? null : data.total === 0 && !filtered ? (
          <div className="empty">
            <Building2 aria-hidden />
            <div>No clinics yet</div>
          </div>
        ) : data.items.length === 0 ? (
          <div className="empty">
            <Search aria-hidden />
            <div>{search.attention ? 'Every clinic is healthy' : 'No clinics match these filters'}</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table rtable health-table">
              <thead>
                <tr>
                  <th>Clinic</th>
                  <th>PF Keeper</th>
                  <th>PF session</th>
                  <th>Last heartbeat</th>
                  <th>Uptime ({HISTORY_DAYS} days)</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((r) => (
                  <HealthRow key={r.clinic.id} row={r} now={now} onOpen={onOpenClinic} link={renderClinicLink} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card status-incidents">
        <div className="card-head">
          <div>
            <h2 className="card-title">Incident history</h2>
            <div className="sub">
              Every time PF Keeper or the Practice Fusion session went down at any clinic in the last {HISTORY_DAYS}{' '}
              days
            </div>
          </div>
          <FilterMenu
            label="Clinic"
            icon={<Building2 aria-hidden />}
            value={search.clinic ?? null}
            allLabel="All clinics"
            options={(clinics ?? []).map((c) => ({ value: c.id, label: c.name }))}
            onChange={(v) => {
              onSearch({ ...search, clinic: v ?? undefined, page: undefined })
            }}
          />
        </div>
        {incidents.isError ? (
          <div className="empty" role="alert">
            <div>Couldn&apos;t load the incidents.</div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => void incidents.refetch()}>
              Retry
            </button>
          </div>
        ) : !list ? (
          <div className="empty" role="status">
            <Activity aria-hidden />
            <div>Loading incidents…</div>
          </div>
        ) : (
          <IncidentList
            items={list.items}
            page={list.page}
            pageSize={list.pageSize}
            total={list.total}
            timeZone={timeZone}
            showClinic
            onPage={(page) => {
              onSearch({ ...search, page: page > 1 ? page : undefined })
            }}
          />
        )}
      </div>
    </>
  )
}

function HealthRow({
  row,
  now,
  onOpen,
  link,
}: {
  row: AdminHealthRow
  now: number
  onOpen: (clinicId: string) => void
  link: (clinic: { id: string; name: string }) => ReactNode
}) {
  const keeper = row.components.find((c) => c.component === 'pf_keeper_app')?.state ?? 'unknown'
  const session = row.components.find((c) => c.component === 'pf_session')?.state ?? 'unknown'
  const connected = row.pf.status !== 'not_connected' && row.pf.status !== 'inactive'
  const pct = pctFromRatio(row.overall.uptime)
  return (
    <tr
      className={`clickable ${row.needsAttention ? 'needs-attention' : ''}`}
      onClick={() => {
        onOpen(row.clinic.id)
      }}
    >
      <td className="c-patient">
        <div className="cell-person">
          <div>
            {link({ id: row.clinic.id, name: row.clinic.name })}
            <small className={`health-pill hp-${row.pf.status}`}>{STATUS_LABEL[row.pf.status]}</small>
          </div>
        </div>
      </td>
      <td className="c-keeper" data-label="PF Keeper">
        <span className={`state-dot sd-${STATE_CLASS[keeper]}`} aria-hidden /> {connected ? SHORT_STATE[keeper] : '—'}
      </td>
      <td className="c-session" data-label="PF session">
        <span className={`state-dot sd-${STATE_CLASS[session]}`} aria-hidden /> {connected ? SHORT_STATE[session] : '—'}
      </td>
      <td className="muted c-beat" data-label="Last heartbeat">
        {!connected
          ? 'Not connected'
          : row.pf.lastHeartbeatAt === null
            ? 'More than 2 days ago'
            : `${heartbeatAge(row.pf.lastHeartbeatAt, now)} ago`}
      </td>
      <td data-label="Uptime" className="c-uptime">
        {connected ? (
          <>
            <span className="pct">{pct === null ? 'No data' : `${pct} %`}</span>
            <UptimeStrip days={row.overall.days} />
          </>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
    </tr>
  )
}
